import React, { FC, PropsWithChildren, createContext, useMemo, useCallback, useRef } from 'react'
import { useRoute,  } from 'react-router5'
import { Router } from 'router5'
import { IApplicationRouter } from '../router'
import { User } from '../types/app'

// Navigation blocker function type - returns true to allow navigation, false to block
export type NavigationBlocker = (routeName: string, routeParams?: Record<string, any>) => boolean

export interface IRouterContext {
  name: string,
  params: Record<string, string>,
  render: () => JSX.Element,
  getTitle?: () => JSX.Element,
  meta: Record<string, any>,
  navigate: {
    (name: string, params?: Record<string, any>): void,
  },
  setParams: {
    /** `replace`: the params become exactly these. `history: 'replace'` swaps the current history entry instead of pushing one. */
    (params: Record<string, string>, replace?: boolean, opts?: { history?: 'push' | 'replace' }): void,
  },
  setUser: {
    (user?: User): void,
  },
  removeParams: {
    /** `history: 'replace'` swaps the current history entry instead of pushing one, so Back does not bring the params back. */
    (params: string[], opts?: { history?: 'push' | 'replace' }): void,
  },
  setNavigationBlocker: (blocker: NavigationBlocker | null) => void,
}

export const CoreRouterContext = createContext<IRouterContext>({
  name: '',
  params: {},
  render: () => <div>Page Not Found</div>,
  meta: {},
  navigate: () => {},
  setParams: () => {},
  setUser: () => {},
  removeParams: () => {},
  setNavigationBlocker: () => {},
})

const RouterContext = CoreRouterContext as any

export const useRouterContext = ({
  router,
  getApplicationRoute,
}: IApplicationRouter, setUser: (user?: User) => void): IRouterContext => {
  const { route } = useRoute()
  const navigationBlockerRef = useRef<NavigationBlocker | null>(null)

  const appRoute = getApplicationRoute()
  const meta = useMemo(() => {
    return appRoute?.meta || {}
  }, [
    appRoute,
  ])

  const setNavigationBlocker = useCallback((blocker: NavigationBlocker | null) => {
    navigationBlockerRef.current = blocker
  }, [])

  const navigate = useCallback((name: string, params?: Record<string, any>) => {
    // Check navigation blocker before navigating
    if (navigationBlockerRef.current) {
      const canNavigate = navigationBlockerRef.current(name, params)
      if (!canNavigate) {
        return // Navigation blocked
      }
    }
    params ?
      router.navigate(name, params) :
      router.navigate(name)
  }, [router])

  const setParams = useCallback((params: Record<string, string>, replace = false, opts?: { history?: 'push' | 'replace' }) => {
    if (!route) return
    const next = replace ? params : Object.assign({}, route.params, params)
    if (opts?.history === 'replace') router.navigate(route.name, next, { replace: true })
    else router.navigate(route.name, next)
  }, [
    route,
    router,
  ])

  const removeParams = useCallback((params: string[], opts?: { history?: 'push' | 'replace' }) => {
    if (!route) return
    // reduce the current params and remove the parans list
    const newParams = Object.keys(route.params).reduce((acc: Record<string, string>, key) => {
      if(params.includes(key)) return acc
      acc[key] = route.params[key]
      return acc
    }, {})
    if (opts?.history === 'replace') router.navigate(route.name, newParams, { replace: true })
    else router.navigate(route.name, newParams)
  }, [
    route,
    router,
  ])

  const render = useCallback(() => {
    return appRoute?.render ? appRoute.render() : <div>Loading...</div>
  }, [
    appRoute,
  ])

  const contextValue = useMemo<IRouterContext>(() => ({
    name: route?.name || '',
    params: route?.params || {},
    meta,
    navigate,
    setParams,
    setUser,
    removeParams,
    render,
    setNavigationBlocker,
  }), [
    route?.name,
    route?.params,
    meta,
    navigate,
    setParams,
    setUser,
    removeParams,
    render,
    setNavigationBlocker,
  ])
  return contextValue
}

export const RouterContextProvider: FC<PropsWithChildren<{
  router: IApplicationRouter,
  setUser: (user?: User) => void,
}>> = ({
  router,
  setUser,
  children,
}) => {
  const value = useRouterContext(router, setUser)
  return (
    <RouterContext.Provider value={ value }>
      { children }
    </RouterContext.Provider>
  )
}